import { mountComputerControl } from './app/components/computer-control.js';
import './computer-control.css';
// ASIS desktop UI integration. Thin shell that wires the session
// controller to the router + views. Kept deliberately small so the
// conversation view (which owns the AsisClient) can be tested in
// isolation against a minimal DOM.

import { registerPwa } from './app/pwa.js';
import { foregroundRefresh } from './app/foreground-refresh.js';
import { getMobileBridge } from './platform/mobile-bridge.js';
import { SessionController, isValidServerBase } from './session/controller.js';
import { Router, type Section } from './app/router.js';
import { renderSidebar } from './app/components/sidebar.js';
import { createConversarView } from './app/views/conversar.js';
import { createEspacioView } from './app/views/espacio.js';
import { createHoyView } from './app/views/hoy.js';
import { createVitalsView } from './app/views/vitales.js';
import { createConectoresView } from './app/views/conectores.js';
import { createModelosView } from './app/views/modelos.js';
import { createDispositivosView } from './app/views/dispositivos.js';
import { createRedesView } from './app/views/redes.js';
import { createMemoriaView } from './app/views/memoria.js';
import { createActividadView } from './app/views/actividad.js';
import { createPerfilView } from './app/views/perfil.js';
import { createAjustesView } from './app/views/ajustes.js';
import { createLoginView, createSetupView } from './app/views/login.js';
import { renderPersonas } from './app/views/personas.js';
import { renderGrupos } from './app/views/grupos.js';
import { renderConocimiento } from './app/views/conocimiento.js';
import { clear, h, toast, type View } from './app/components/dom.js';
import { accountEvents, taskFinishedText } from './app/account-events.js';
import type { KnowledgeScope } from './api/people-types.js';
import type { AuthState } from './session/controller.js';
import { startComputerStatusSync, type ComputerStatusSync } from './app/computer-status-sync.js';
import { createDesktopTidyController, type DesktopTidyController } from './app/components/desktop-tidy.js';

export interface AppHandle {
  controller: SessionController;
  router: Router;
  state: AuthState;
  refresh: () => void;
  destroy: () => void;
}

interface BridgeUnsubscribe {
  (): void;
}

const DEFAULT_BROWSER_BASE = (typeof window !== 'undefined' && window.location?.origin)
  ? window.location.origin
  : 'http://localhost:5173';
const DEFAULT_DESKTOP_BASE = 'https://app.asisdot.com';

function serverBaseStore() {
  const KEY = 'asis.serverBase';
  return {
    read: () => {
      try { return globalThis.localStorage?.getItem(KEY) ?? null; } catch { return null; }
    },
    write: (v: string) => {
      try { globalThis.localStorage?.setItem(KEY, v); } catch { /* ignore */ }
    },
    clear: () => {
      try { globalThis.localStorage?.removeItem(KEY); } catch { /* ignore */ }
    },
  };
}

export async function bootstrap(): Promise<AppHandle> {
  const main = document.getElementById('main');
  if (!main) throw new Error('missing #main');
  const controller = new SessionController({
    defaultBrowserBase: DEFAULT_BROWSER_BASE,
    defaultDesktopBase: DEFAULT_DESKTOP_BASE,
    serverBaseStore: serverBaseStore(),
    logger: (m, d) => console.log('[asis]', m, d),
  });
  document.documentElement.toggleAttribute('data-electron', controller.isElectron());
  const router = new Router();
  let currentAuth: AuthState = { kind: 'unknown' };
  let currentView: View | null = null;
  let requestedKnowledgeScope: KnowledgeScope | undefined;
  let renderGeneration = 0;
  let voiceConfig: Awaited<ReturnType<ReturnType<SessionController['getApi']>['getVoiceConfig']>> | null = null;
  let version: string | null = null;
  let computerStatusSync: ComputerStatusSync | null = null;
  let desktopTidy: DesktopTidyController | null = null;
  let computerControl:ReturnType<typeof mountComputerControl>|null=null;
  let computerHeartbeat:ReturnType<typeof setInterval>|undefined;
  let computerClientId:string|null=null;
  let computerHost:HTMLElement|null=null;
  function stopComputerControl(){
    if(computerHeartbeat)clearInterval(computerHeartbeat);computerHeartbeat=undefined;
    if(computerControl)void window.asisDesktop?.computerControl?.stop().catch(()=>{});
    computerControl?.destroy();computerControl=null;computerClientId=null;
    computerHost?.remove();computerHost=null;
  }

  const authorized = () => currentAuth.kind === 'authenticated' || currentAuth.kind === 'local';

  function disposeView(): void { currentView?.destroy?.(); currentView = null; }

  let suspended = false;
  let destroyed = false;
  let foreground = true;
  const mobile = getMobileBridge();
  function clearPrivateDom(): void {
    document.getElementById('toast-host')?.replaceChildren();
    document.querySelectorAll('.modal-overlay').forEach(el => el.remove());
  }

  function buildPausedMessage(): void {
    if (!main) return;
    disposeView();
    clear(main);
    main.className = 'main auth-main';
    const host = h('div', { class: 'auth-card paused' }, [
      h('p', { role: 'status' }, ['ASIS en pausa']),
      h('p', {}, ['La conversación se detuvo al salir de la app. Toca Continuar cuando quieras seguir.']),
      h('button', {
        type: 'button',
        class: 'btn primary',
        onclick: () => { resumeFromBackground(); },
      }, ['Continuar']),
    ]);
    main.appendChild(host);
    currentView = {
      el: host,
      update() { /* paused: noop */ },
      destroy() { host.remove(); },
    };
    main.setAttribute('aria-busy', 'false');
  }

  function resumeFromBackground(): void {
    if (destroyed || !foreground || !suspended) return;
    suspended = false;
    ++renderGeneration;
    syncEvents();
    if (!authorized()) { buildLayout(currentAuth); return; }
    buildLayout({ kind: 'unknown' });
    const generation = renderGeneration;
    void Promise.allSettled([controller.getApi().getVoiceConfig(undefined, currentAuth.kind === 'local'), controller.getVersion()]).then(([config, appVersion]) => {
      if (destroyed || generation !== renderGeneration || suspended) return;
      voiceConfig = config.status === 'fulfilled' ? config.value : null;
      version = appVersion.status === 'fulfilled' ? appVersion.value : null;
      if (router.currentSection() === 'login' || router.currentSection() === 'setup') router.go(currentAuth.kind === 'local' ? 'conversar' : 'hoy');
      buildLayout(currentAuth);
    });
  }

  function buildLayout(auth: AuthState): void {
    if (!main) return;
    disposeView(); clear(main);
    if (auth.kind === 'unknown') {
      main.className = 'main auth-main';
      main.appendChild(h('p', { class: 'boot', role: 'status' }, ['Conectando con tu servidor…']));
      return;
    }
    if (auth.kind === 'anonymous' && auth.reason !== 'setup_required') {
      main.className = 'main auth-main';
      const view = createLoginView(controller, auth);
      main.appendChild(view.el);
      currentView = view;
      main.setAttribute('aria-busy', 'false');
      return;
    }
    if (auth.kind === 'anonymous' && auth.reason === 'setup_required') {
      main.className = 'main auth-main';
      const view = createSetupView(controller);
      main.appendChild(view.el);
      currentView = view;
      main.setAttribute('aria-busy', 'false');
      return;
    }
    main.className = 'main desktop-main';
    const account = controller.getUser() ? { displayName: controller.getUser()!.displayName, username: controller.getUser()!.username, email: controller.getUser()!.email, onLogout: () => { void controller.logout().catch(err => toast((err as Error).message, 'error')); } } : undefined;
    const sidebar = renderSidebar(router.currentSection(), (s) => router.go(s), account);
    main.appendChild(sidebar);
    if(computerHost)sidebar.append(computerHost);
    const content = document.createElement('div');
    content.className = 'content';
    content.id = 'content';
    main.appendChild(content);
    const selected = currentAuth.kind === 'local' && router.currentSection() === 'hoy' ? 'conversar' : router.currentSection();
    mountSection(selected, content);
    main.setAttribute('aria-busy', 'false');
  }

  function mountSection(section: Section, host: HTMLElement): void {
    disposeView();
    clear(host);
    const view = buildView(section);
    if (!view) return;
    host.appendChild(view.el);
    currentView = view;
  }

  function buildView(section: Section): View | null {
    const user = controller.getUser();
    const desktopLocal = mobile ? undefined : window.asisDesktop?.local;
    if (section === 'conversar') {
      return createConversarView({
        serverBase: controller.getServerBase(),
        voiceConfig,
        getToken: () => controller.getToken() ?? voiceConfig?.bootstrapToken ?? null,
        userDisplayName: user?.displayName ?? null,
        api: currentAuth.kind==='authenticated'?controller.getApi():undefined,
        localBridge: currentAuth.kind==='authenticated'?desktopLocal:undefined,
        log: (m, d) => console.log('[asis]', m, d),
      });
    }
    if (currentAuth.kind === 'local' && section !== 'ajustes') return { el: h('section', { class: 'view' }, [h('h1', {}, ['Cuenta necesaria']), h('p', {}, ['Hoy, dispositivos, memoria, historial, conectores y modelos requieren un servidor con cuenta. Cambia el servidor en Ajustes.'])]) };
    const api = controller.getApi();
    const nav = { navigate: (s: string, scope?: KnowledgeScope) => { requestedKnowledgeScope = scope; router.go(s as Section); } };
    if (section === 'espacio') return createEspacioView(controller);
    if (section === 'hoy') return createHoyView(controller);
    if (section === 'escaneo') return createVitalsView(controller);
    if (section === 'personas') return renderPersonas({ api: () => api, navigate: nav.navigate, ...(desktopLocal ? {local:desktopLocal} : {}) });
    if (section === 'grupos') return renderGrupos({ ...nav, api: () => api, userId: controller.getUser()?.id ?? '' });
    if (section === 'conocimiento') return renderConocimiento({ api: () => api, userId: controller.getUser()?.id ?? '', ...(requestedKnowledgeScope ? {initialScope:requestedKnowledgeScope} : {}) });
    if (section === 'conectores') return createConectoresView(controller);
    if (section === 'modelos') return createModelosView(controller);
    if (section === 'dispositivos') return createDispositivosView(controller);
    if (section === 'redes') return createRedesView(controller);
    if (section === 'memoria') return createMemoriaView(controller);
    if (section === 'actividad') return createActividadView(controller);
    if (section === 'perfil') return createPerfilView(controller);
    if (section === 'ajustes') return createAjustesView(controller, { version, isElectron: controller.isElectron(), desktopTidy });
    return null;
  }

  // Parallel tasks (from the app or by voice) report here when they finish, whatever section is open.
  const syncEvents = () => {
    stopComputerControl();
    computerStatusSync?.stop(); computerStatusSync = null;
    desktopTidy?.destroy(); desktopTidy = null;
    if (!destroyed && !suspended && currentAuth.kind === 'authenticated') {
      accountEvents.start(controller.getApi());
      if(!mobile&&window.asisDesktop?.computerControl){
        const bridge=window.asisDesktop.computerControl;
        computerClientId=crypto.randomUUID();const clientId=computerClientId;
        computerHost=h('aside',{class:'computer-control-host','aria-label':'Permisos de este Mac'});
        // Attached to the persistent sidebar by buildLayout, never over the composer.
        computerControl=mountComputerControl({host:computerHost,desktop:bridge,api:controller.getApi()});
        const beat=()=>{if(computerClientId!==clientId)return;void controller.getApi().computerPresence(clientId).catch(()=>{void bridge.stop();});};
        beat();computerHeartbeat=setInterval(beat,5000);
      }
      if (!mobile && window.asisDesktop?.computer) computerStatusSync = startComputerStatusSync(controller.getApi(), window.asisDesktop.computer);
      if (!mobile && window.asisDesktop?.desktop) desktopTidy = createDesktopTidyController({ bridge: window.asisDesktop.desktop });
    } else accountEvents.stop();
  };
  const unsubscribeEvents = accountEvents.subscribe(event => {
    if(event.type==='computer.action_requested'){if(event.clientId===computerClientId)void computerControl?.handle(event);return;}
    if(event.type==='computer.action_cancelled'){if(event.clientId===computerClientId)void window.asisDesktop?.computerControl?.stop();return;}

    if (event.type === 'desktop.plan_requested') { void desktopTidy?.open(); return; }
    const text = taskFinishedText(event);
    if (text && event.type === 'task.finished' && !destroyed && !suspended) toast(text, event.task.status === 'succeeded' ? 'ok' : 'info', 8000);
  });
  const unsubscribe = controller.onState((s) => {
    const generation = ++renderGeneration;
    if (destroyed) return;
    accountEvents.stop();
    disposeView();
    clearPrivateDom();
    voiceConfig = null; requestedKnowledgeScope = undefined;
    currentAuth = s;
    if (suspended) { buildPausedMessage(); return; }
    syncEvents();
    if (authorized()) {
      buildLayout({ kind: 'unknown' });
      void Promise.allSettled([controller.getApi().getVoiceConfig(undefined, s.kind === 'local'), controller.getVersion()]).then(([config, appVersion]) => {
        if (destroyed || generation !== renderGeneration || suspended) return;
        voiceConfig = config.status === 'fulfilled' ? config.value : null;
        version = appVersion.status === 'fulfilled' ? appVersion.value : null;
        // Router.start already validates the hash. Keep a restored private
        // section; only authentication pages need an authenticated fallback.
        if (router.currentSection() === 'login' || router.currentSection() === 'setup') router.go(currentAuth.kind === 'local' ? 'conversar' : 'hoy');
        buildLayout(s);
      });
    } else { buildLayout(s);
    }
  });

  router.setOnChange((next) => {
    if (!authorized() || suspended || destroyed) return;
    if (next === 'login' || next === 'setup') { router.go(currentAuth.kind === 'local' ? 'conversar' : 'hoy'); return; }
    buildLayout(currentAuth);
  });

  let mobileUnsubscribe: BridgeUnsubscribe | null = null;

  if (mobile) {
    document.documentElement.dataset.nativePlatform = mobile.platform;
    mobileUnsubscribe = mobile.onAppStateChange((active) => {
      if (destroyed) return;
      foreground = active;
      if (!active) {
        suspended = true;
        ++renderGeneration;
        accountEvents.stop();
        computerStatusSync?.stop(); computerStatusSync = null;
        desktopTidy?.destroy(); desktopTidy = null;
        disposeView();
        clearPrivateDom();
        if (main) clear(main);
        buildPausedMessage();
      }
      // foreground handled only by explicit Continuar click
    });
  }

  // initial bootstrap: probe server, then render
  router.start('auth');
  await controller.bootstrap();
  void registerPwa();
  const stopRefresh = foregroundRefresh(() => { if (!suspended && !destroyed) currentView?.update?.({}); }, () => !suspended && !destroyed && currentAuth.kind === 'authenticated' && ['hoy', 'memoria', 'actividad', 'dispositivos'].includes(router.currentSection()));

  return {
    controller,
    router,
    get state() { return currentAuth; },
    refresh: () => { if (!suspended && !destroyed) currentView?.update?.({}); },
    destroy: () => {
      destroyed = true;
      suspended = false;
      ++renderGeneration;
      if (currentView) { currentView.destroy?.(); currentView = null; }
      stopRefresh();
      unsubscribe();
      stopComputerControl();
      accountEvents.stop(); unsubscribeEvents();
      computerStatusSync?.stop(); computerStatusSync = null;
      desktopTidy?.destroy(); desktopTidy = null;
      if (mobileUnsubscribe) { try { mobileUnsubscribe(); } catch { /* ignore */ } mobileUnsubscribe = null; }
      delete document.documentElement.dataset.nativePlatform;
      controller.getApi().invalidate();
      router.stop();
    },
  };
}

// kick off when used standalone (not in tests where the test
// re-invokes bootstrap() against a fresh DOM)
if (typeof document !== 'undefined' && document.getElementById('app')) {
  void bootstrap();
}

// re-export for tests
export { isValidServerBase };
